import { useMemo } from 'react';

import PropTypes from 'prop-types';

import { Select } from '@utilities';

import { DEFAULT_OPTION, SORT_ORDER_OPTIONS } from '../../utilities/constants';

import styles from './_index.module.scss';

const SortControls = ({
  compareByGroups,
  compareByValue,
  isDisabled,
  onSortByChange,
  onSortOrderChange,
  sortByValue,
  sortOrder,
}) => {
  const isOverall = compareByValue === 'overall';

  const sortByOptions = useMemo(() => {
    if (!compareByGroups?.length) return [DEFAULT_OPTION];

    return [
      DEFAULT_OPTION,
      ...compareByGroups.map(({ id, name }) => ({
        label: name,
        value: String(id),
      })),
    ];
  }, [compareByGroups]);

  const sortBySelectedOption = useMemo(() => {
    return sortByOptions.find((opt) => opt.value === sortByValue) ?? sortByOptions[0] ?? null;
  }, [sortByOptions, sortByValue]);

  const sortOrderOptions = useMemo(() => {
    return isOverall
      ? SORT_ORDER_OPTIONS
      : SORT_ORDER_OPTIONS.filter((opt) => opt.value !== 'default');
  }, [isOverall]);

  const sortOrderSelectedOption = useMemo(
    () => sortOrderOptions.find((opt) => opt.value === sortOrder) ?? sortOrderOptions?.[0] ?? null,
    [sortOrderOptions, sortOrder]
  );

  const hasSortBySelection = sortByValue !== 'default';
  const showSortOrder = isOverall || hasSortBySelection;
  const canClearSortOrder = isOverall && sortOrder !== 'default';
  const canClearSortBy = !isOverall && sortByValue !== 'default';

  return (
    <div className={styles['sort-controls-row']}>
      {!isOverall && !isDisabled && (
        <div className={styles['sort-controls-group']}>
          <div className={styles['sort-controls-select-wrapper']}>
            <Select
              className={styles['sort-controls-select']}
              isClearable={canClearSortBy}
              isDisabled={isDisabled}
              isLoading={false}
              label="SORT BY"
              onChange={(opt) => {
                if (!opt) {
                  onSortByChange?.('default');
                  onSortOrderChange?.('default');
                  return;
                }
                onSortByChange?.(opt.value);

                if (sortOrder === 'default') {
                  onSortOrderChange?.('asc');
                }
              }}
              options={sortByOptions}
              value={sortBySelectedOption}
            />
          </div>
        </div>
      )}

      {!isDisabled && showSortOrder && (
        <div className={styles['sort-controls-group']}>
          <div className={styles['sort-controls-select-wrapper']}>
            <Select
              className={styles['sort-controls-select']}
              isClearable={canClearSortOrder}
              isDisabled={isDisabled}
              isLoading={false}
              label="SORT ORDER"
              onChange={(opt) => onSortOrderChange?.(opt?.value ?? 'default')}
              options={sortOrderOptions}
              value={sortOrderSelectedOption}
            />
          </div>
        </div>
      )}
    </div>
  );
};

SortControls.defaultProps = {
  compareByGroups: [],
  compareByValue: 'overall',
  isDisabled: false,
  sortByValue: 'default',
  sortOrder: 'default',
};

SortControls.propTypes = {
  compareByGroups: PropTypes.arrayOf(
    PropTypes.shape({
      color: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    })
  ),
  compareByValue: PropTypes.string,
  isDisabled: PropTypes.bool,
  onSortByChange: PropTypes.func,
  onSortOrderChange: PropTypes.func,
  sortByValue: PropTypes.string,
  sortOrder: PropTypes.oneOf(['default', 'asc', 'desc']),
};

export default SortControls;
